import { FC, useEffect, useRef } from "react";
import { useViewListContext } from "@/context/ViewListContext";
import { AlternatingMessages, Button, LoaderBar, useGoBack } from "@vixi/ui";
import { useCourseJobStatus } from "@/hooks/api/course/useCourseJobStatus";
import { useTranslation } from "@vixi/localization";
import { DevOnly } from "@/components/DevOnly";
import { Img } from "@vixi/ui";
import { CourseData, CoursemakerUploadResponse } from "@vixi/types";
import { courseGenerationToCourse, forEachLesson, logger } from "@vixi/utils";
import api from "@vixi/api";

// Assets
import CourseGeneratedBubbleEn from "@assets/bubbles/course-generated.png";

// Constants
const CourseGeneratedBubble = {
  en: CourseGeneratedBubbleEn,
} as const;

const setDefaultSelectedTitle = (lesson: CourseData.Lesson) => {
  if (lesson.selectedTitle === undefined) {
    lesson.selectedTitle = 0;
  }
};

interface CourseProgressStepProps {
  jobId: string;
  retry: () => void;
  onComplete: (courseData: CourseData.Course) => void;
}

export const CourseProgressStep: FC<CourseProgressStepProps> = ({
  jobId,
  retry,
  onComplete,
}) => {
  useGoBack(null);
  const { t, tFile, byLocale } = useTranslation();
  const mainFlow = useViewListContext();

  const { data: jobStatus, isError, error, isLoading } = useCourseJobStatus(jobId, true);
  const hasFetchedResult = useRef(false);

  // Check if job is completed and fetch course data
  // IMPORTANT: We only fetch course data when:
  // 1. completedAt is not null (job has finished - this is the main indicator)
  // 2. No error message (job completed successfully)
  // 3. We haven't fetched the result yet (prevent duplicate calls)
  useEffect(() => {
    if (
      jobStatus &&
      jobStatus.completedAt !== null && // Job has finished timestamp (main indicator)
      !jobStatus.errorMessage && // No error occurred
      !hasFetchedResult.current // Haven't fetched result yet
    ) {
      hasFetchedResult.current = true;
      
      // Check if result is already in the response (from GET /api/CourseMaker/jobs/{jobId})
      if (jobStatus.result && Array.isArray(jobStatus.result) && jobStatus.result.length > 0) {
        logger.info(`✅ STEP 3: Course data already in job status response, using it directly`);
        
        // Transform API response to CourseData format
        const courseData = courseGenerationToCourse(jobStatus.result);
        forEachLesson(courseData, setDefaultSelectedTitle);

        logger.info("✅ STEP 3 SUCCESS: Course data transformed and ready");
        onComplete(courseData);
        return;
      }
      
      // ============================================
      // STEP 3: GET /api/CourseMaker/jobs/{jobId}/result
      // ============================================
      // This is the THIRD request - ONLY when job is completed and result is not in status response
      // Response: CourseGeneration.Course (the actual course data)
      // This is the ONLY place where we fetch course data - after job completion
      logger.info(`📥 STEP 3: Job completed! Fetching course data from GET /api/CourseMaker/jobs/${jobId}/result`);
      
      const fetchCourseResult = async () => {
        try {
          const endpoint = api.endpoints.getCourseJobResult(jobId);
          const baseURL = api.getDefaultConfig().baseURL || "/api";
          const fullURL = `${baseURL}/${endpoint}`;
          
          logger.info(`📥 STEP 3: Calling GET ${fullURL}`);
          
          const response = await api.get<CoursemakerUploadResponse>(endpoint);
          
          logger.info("✅ STEP 3 COMPLETE: Received course data from API");
          logger.info("✅ Course data length:", response.data?.length);
          
          // Transform API response to CourseData format
          const courseData = courseGenerationToCourse(response.data);
          forEachLesson(courseData, setDefaultSelectedTitle);

          logger.info("✅ STEP 3 SUCCESS: Course data transformed and ready");
          onComplete(courseData);
        } catch (err) {
          logger.error("❌ STEP 3 FAILED: Failed to fetch course result", err);
          hasFetchedResult.current = false; // Reset on error to allow retry
        }
      };

      fetchCourseResult();
    }
  }, [jobStatus, jobId, onComplete]);

  const hasError = isError || (jobStatus?.errorMessage && jobStatus.errorMessage.length > 0);
  const isCompleted =
    jobStatus && jobStatus.completedAt !== null && !hasError;
  const progressPercentage = jobStatus?.progressPercentage ?? 0;
  const progressMessage = jobStatus?.progressMessage ?? "";

  if (hasError) {
    return (
      <div className="w-105 text-center">
        <Img contain src={tFile("/images", "bubble-generating-course.png")} className="w-105" />

        <p className="text-error">
          {jobStatus?.errorMessage || t("ErrorMessage.SomethingWentWrong")}
        </p>

        <LoaderBar className="mx-auto my-2.5" error={true} width={400} />

        {/* Show error message in development */}
        <DevOnly>
          <p className="text-error mb-2.5">{error?.message}</p>
        </DevOnly>

        <Button variant="contained" size="large" onClick={retry} className="mt-0.5">
          {t("Action.TryAgain")}
        </Button>
      </div>
    );
  }

  if (isCompleted) {
    // This will be handled by the useEffect above, but show success UI
    return (
      <>
        <Img
          src={byLocale({
            ...CourseGeneratedBubble,
            fallback: tFile("/images", "bubble-congrats.png"),
          })}
          className="w-110"
        />
        <Button
          variant="contained"
          size="large"
          type="submit"
          onClick={mainFlow?.nextStep}
          className="absolute end-20 bottom-12.5"
        >
          {t("Action.Next")}
        </Button>
      </>
    );
  }

  // Show progress
  return (
    <div className="w-105 text-center">
      <Img contain src={tFile("/images", "bubble-generating-course.webp")} className="w-105" />

      {/* Progress Bar with Percentage */}
      <div className="mx-auto my-2.5" style={{ width: 400 }}>
        {/* Progress Bar Container */}
        <div
          className="bg-paper flex h-11 items-center justify-center overflow-hidden rounded p-1.5 relative"
          style={{ width: 400 }}
        >
          <div className="relative h-full w-full overflow-hidden rounded">
            {progressPercentage === 100 ? (
              <div
                className="loading-bar-determinate bg-primary h-full rounded transition-all"
                style={{ width: "100%" }}
              />
            ) : (
              <div
                className="loading-bar-determinate bg-primary h-full rounded transition-all"
                style={{ width: `${progressPercentage}%` }}
              />
            )}
          </div>
          
          {/* Percentage Text Overlay */}
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-sm font-semibold text-foreground z-10">
              {Math.round(progressPercentage)}%
            </span>
          </div>
        </div>
      </div>

      {/* Progress Message and Status */}
      {isLoading ? (
        <p>{t("LoadingMessage.UploadingFile")}</p>
      ) : (
        <>
          {progressMessage && (
            <p className="mb-2 font-medium">{progressMessage}</p>
          )}
          {jobStatus && jobStatus.totalPages > 0 && (
            <p className="text-sm text-muted-foreground mb-2">
              {Math.round(progressPercentage)}% • {jobStatus.totalPages} pages
            </p>
          )}
          <p>
            <AlternatingMessages
              messages={[
                "LoadingMessage.GrabSnack",
                "LoadingMessage.CourseInMaking",
                "LoadingMessage.Stretch",
              ]}
            />
          </p>
        </>
      )}
    </div>
  );
};
